'use strict';

var bodyScrollLock = require('body-scroll-lock');
var zod = require('zod');

// src/responsive-helper.ts
var ResponsiveHelper = class {
  sm;
  md;
  lg;
  constructor() {
    this.sm = 768;
    this.md = 1024;
    this.lg = 1200;
  }
  /**
   * Check if user is in the mobile viewport (768px <=)
   */
  isMobile() {
    const windowWidth = window.innerWidth;
    return windowWidth <= this.sm - 1;
  }
  /**
   * Check if user is in the tablet portrait viewport (768px >= && <= 991px)
   */
  isTabletPortrait() {
    const windowWidth = window.innerWidth;
    return windowWidth >= this.sm && windowWidth <= 991;
  }
  /**
   * Check if user is in the tablet landscape viewport (992px >= && <= 1024px)
   */
  isTabletLandscape() {
    const windowWidth = window.innerWidth;
    return windowWidth >= 992 && windowWidth <= this.md;
  }
  /**
   * Check if user is in the desktop viewport (1200px >=)
   */
  isDesktop() {
    const windowWidth = window.innerWidth;
    return windowWidth >= this.lg;
  }
  /**
   * Check if user is atleast in the tablet portrait viewport (768px >=)
   */
  atLeastTabletPortrait() {
    const windowWidth = window.innerWidth;
    return windowWidth >= this.sm;
  }
  /**
   * Check if user is smaller than the tablet portrait viewport (768px <=)
   */
  smallerThanTabletPortrait() {
    const windowWidth = window.innerWidth;
    return windowWidth <= this.sm;
  }
  /**
   * Check if user is atleast in the tablet landscape viewport (992px >=)
   */
  atLeastTabletLandscape() {
    const windowWidth = window.innerWidth;
    return windowWidth >= 992;
  }
  /**
   * Check if user is smaller than the tablet landscape viewport (992px <=)
   */
  smallerThanTabletLandscape() {
    const windowWidth = window.innerWidth;
    return windowWidth <= 992;
  }
  /**
   * Check if user viewport is smaller than custom size (e.g. 900px <=)
   *
   * @param  size
   */
  smallerThanCustomSize(size) {
    const windowWidth = window.innerWidth;
    return windowWidth <= size;
  }
  /**
   * Check if user is atleast in custom size viewport (e.g. 900px >=)
   *
   * @param  size
   */
  atLeastCustomSize(size) {
    const windowWidth = window.innerWidth;
    return windowWidth >= size;
  }
  /**
   * Check if user is on a real device
   */
  isRealDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && typeof window.orientation !== "undefined";
  }
};

// src/focusable-elements.ts
var getAllFocusableElements = (parent) => {
  let items = Array.from(
    parent.querySelectorAll(
      `
		iframe,
		object,
		embed,
		a[href],
		area[href],
		button,
		input,
		select,
		textarea,
		details,
		summary:not(:disabled),
		*[contenteditable]
		`
    )
  );
  items = items.filter((item) => {
    const aria = item.getAttribute("aria-hidden");
    const ariaHidden = aria === "true" ? true : false;
    if (window.getComputedStyle(item, null).display === "none") {
      return false;
    }
    return !item.hasAttribute("disabled") && !ariaHidden;
  });
  items = items.filter((item) => {
    const aria = item.closest("[aria-hidden]")?.getAttribute("aria-hidden");
    const ariaHidden = aria === "true" ? true : false;
    return !ariaHidden;
  });
  return items.filter((item) => {
    return item.getAttribute("tabindex") !== "-1";
  });
};
var getFirstFocusableElement = (parent) => {
  const all = getAllFocusableElements(parent);
  return all[0];
};

// src/tab-focus-helper.ts
var TabFocusHelper = class {
  /**
   * Trap the forward/back tab inside the given element
   *
   * @param  element
   * @param  event
   */
  trap(element, event) {
    const focusableItems = getAllFocusableElements(element);
    const focusedItem = document.activeElement;
    const numberOfFocusableItems = focusableItems.length;
    const focusedItemIndex = focusableItems.findIndex((item) => item === focusedItem);
    if (event.shiftKey) {
      if (focusedItemIndex === 0) {
        const lastFocusableItem = focusableItems[numberOfFocusableItems - 1];
        lastFocusableItem?.focus();
        event.preventDefault();
      }
    } else if (focusedItemIndex === numberOfFocusableItems - 1) {
      if (focusableItems.length > 0) {
        const firstFocusableItem = getFirstFocusableElement(element);
        firstFocusableItem.focus();
        event.preventDefault();
      }
    }
  }
};
var MrModal = class {
  element;
  options;
  config;
  main;
  modal;
  closeBtns;
  overlay;
  tabFocus;
  constructor(element, options) {
    this.element = element;
    this.options = options;
    this.main = document.querySelector("main");
    this.config = this.element?.dataset.config ? JSON.parse(this.element.dataset.config) : {};
    this.tabFocus = new TabFocusHelper();
    this.modal = null;
    this.closeBtns = null;
    this.overlay = null;
    if (element && this.config?.modalID) {
      this.modal = document.getElementById(this.config.modalID);
      this.closeBtns = this.modal ? Array.from(this.modal.querySelectorAll(".btn--js-modal-close")) : null;
      this.overlay = this.modal ? this.modal.querySelector(".modal__overlay") : null;
    }
    if (!element && this.options?.modalId) {
      this.modal = document.getElementById(this.options?.modalId);
      this.closeBtns = this.modal ? Array.from(this.modal.querySelectorAll(".btn--js-modal-close")) : null;
      this.overlay = this.modal ? this.modal.querySelector(".modal__overlay") : null;
    }
    if (!this.element && !this.options?.modalId) {
      console.warn('MrModal: when not using a DOM element as the Modal trigger, you must supply the "modalId" property');
      return;
    }
    if (this.element && !this.config?.modalID) {
      console.warn("MrModal: modalID property not found in data-config");
      return;
    }
    if (this.element && !this.modal) {
      console.warn("MrModal: no modal found with given modalID");
      return;
    }
    if (this.modal && this.options?.closeBtn) {
      this.closeBtns = Array.from(this.modal.querySelectorAll(`.${this.options.closeBtn}`));
    }
    this.initialize();
  }
  initialize() {
    if (this.element) {
      this.element.addEventListener("click", this.open.bind(this));
    }
    this.closeBtns?.forEach((closeButton) => {
      closeButton.addEventListener("click", this.close.bind(this));
    });
    this.overlay?.addEventListener("click", this.close.bind(this));
    this.modal?.addEventListener("keydown", this.keyDown.bind(this));
  }
  open() {
    if (!this.modal) {
      return;
    }
    this.main?.setAttribute("aria-hidden", "true");
    this.modal.setAttribute("aria-hidden", "false");
    if (this.element) {
      window.modalTrigger = this.element;
    }
    const firstTabbable = getFirstFocusableElement(this.modal);
    setTimeout(() => {
      firstTabbable?.focus();
    }, 50);
    if (this.element) {
      this.element.dispatchEvent(new Event("openMrModal"));
    } else {
      document.dispatchEvent(new Event("openMrModal"));
    }
    if (this.config.disableScroll) {
      let customScrollableElement = null;
      if (this.config.customScrollableElement) {
        customScrollableElement = document.querySelector(this.config.customScrollableElement);
      }
      if (customScrollableElement) {
        bodyScrollLock.disableBodyScroll(customScrollableElement);
      } else {
        bodyScrollLock.disableBodyScroll(this.modal);
      }
    }
  }
  close() {
    if (!this.modal) {
      return;
    }
    this.main?.setAttribute("aria-hidden", "false");
    this.modal.setAttribute("aria-hidden", "true");
    window.modalTrigger?.focus();
    if (this.config.disableScroll) {
      bodyScrollLock.clearAllBodyScrollLocks();
    }
    if (this.element) {
      this.element.dispatchEvent(new Event("closeMrModal"));
    } else {
      document.dispatchEvent(new Event("closeMrModal"));
    }
  }
  keyDown(event) {
    if (event.key === "Tab" && this.modal) {
      this.tabFocus.trap(this.modal, event);
    }
    if (event.key === "Escape") {
      this.close();
    }
  }
};
var configDefaults = {
  trigger: null,
  target: null,
  disableScroll: false,
  disableTrap: false,
  customScrollableElement: null,
  focusInputClass: null,
  focusInput: false,
  defaultButtonText: null,
  closeButtonText: null,
  isChildToggle: false
};
var MrToggle = class {
  element;
  config;
  options;
  _moduleName;
  _triggers;
  _targets;
  _tabFocus;
  _responsiveHelper;
  _buttonTextTarget;
  constructor(element, options) {
    this.element = element;
    this.options = options;
    this._moduleName = this.options?.moduleName ? this.options.moduleName : "";
    this.config = this.element.dataset.config ? { ...configDefaults, ...JSON.parse(this.element.dataset.config) } : {};
    this._triggers = [];
    this._targets = [];
    this._tabFocus = new TabFocusHelper();
    this._responsiveHelper = new ResponsiveHelper();
    this._buttonTextTarget = ".btn__text, [data-button-text]";
    if (typeof this.config.trigger === "undefined") {
      console.warn("MrToggle: Trigger property not found in data-config");
      return;
    }
    if (typeof this.config.target === "undefined") {
      console.warn("MrToggle: Target property not found in data-config");
      return;
    }
    this.reset();
    this.addEventListeners();
  }
  /**
   * Loop though all toggle modules and add them to two arrays: _triggers and _targets
   * These items will be used to reset any other toggles
   */
  reset() {
    if (this._moduleName?.length === 0) {
      return;
    }
    const modules = Array.from(document.querySelectorAll(`[data-module="${this._moduleName}"]`));
    modules.forEach((item) => {
      const itemConfig = item.dataset.config ? { ...configDefaults, ...JSON.parse(item.dataset.config) } : null;
      if (!itemConfig) {
        return;
      }
      if (itemConfig.trigger) {
        const trigger = document.querySelector(itemConfig.trigger);
        if (trigger) {
          this._triggers.push(trigger);
        }
      }
      if (itemConfig.target) {
        const target = document.querySelector(itemConfig.target);
        if (target) {
          this._targets.push(target);
        }
      }
    });
  }
  /**
   * Close all toggles
   */
  closeAllToggles() {
    this.close();
  }
  /**
   * Adds event listeners
   */
  addEventListeners() {
    if (!this.config.trigger) {
      return;
    }
    document.querySelectorAll(this.config.trigger).forEach((trigger) => {
      trigger.addEventListener("click", this.handleClick.bind(this));
    });
    this.element.addEventListener("keydown", this.handleKeyboard.bind(this));
    if (this.options?.useHover && !this._responsiveHelper.isRealDevice()) {
      this.element.addEventListener("mouseenter", this.open.bind(this));
      this.element.addEventListener("mouseleave", this.handleMouseLeave.bind(this));
    }
  }
  /**
   * Call open or close function based on aria-expanded
   */
  handleClick() {
    if (!this.config.trigger) {
      return;
    }
    if (document.querySelector(this.config.trigger)?.getAttribute("aria-expanded") === "false") {
      this.open();
    } else {
      this.close();
    }
  }
  /**
   * Opens a toggle
   */
  open() {
    if (!this.config.trigger || !this.config.target) {
      return;
    }
    if (!this.config.isChildToggle) {
      this.resetAllToggles();
    }
    const trigger = document.querySelector(this.config.trigger);
    const target = document.querySelector(this.config.target);
    if (!trigger || !target) {
      return;
    }
    trigger.setAttribute("aria-expanded", "true");
    const dataExpanded = trigger.getAttribute("data-expanded");
    if (dataExpanded) {
      trigger.setAttribute("aria-label", dataExpanded);
    }
    target.setAttribute("aria-hidden", "false");
    if (this.config.disableScroll) {
      let customScrollableElement = null;
      if (this.config.customScrollableElement) {
        customScrollableElement = document.querySelector(this.config.customScrollableElement);
      }
      if (customScrollableElement) {
        bodyScrollLock.disableBodyScroll(customScrollableElement);
      } else {
        bodyScrollLock.disableBodyScroll(target);
      }
    }
    if (this.config.closeButtonText && this.config.defaultButtonText) {
      const btnText = trigger.querySelector(this._buttonTextTarget);
      if (btnText instanceof HTMLElement) {
        btnText.textContent = this.config.closeButtonText;
      }
    }
    if (this.config.focusInput) {
      setTimeout(() => {
        let $input = target.querySelector("input");
        if (this.config.focusInputClass) {
          $input = target.querySelector(this.config.focusInputClass);
        }
        $input?.focus();
      }, 100);
    }
    this.element.dispatchEvent(new Event("openMrToggle"));
  }
  close() {
    if (!this.config.trigger || !this.config.target) {
      return;
    }
    const trigger = document.querySelector(this.config.trigger);
    const target = document.querySelector(this.config.target);
    if (!trigger || !target) {
      return;
    }
    trigger.setAttribute("aria-expanded", "false");
    const dataCollapsed = trigger.getAttribute("data-collapsed");
    if (dataCollapsed) {
      trigger.setAttribute("aria-label", dataCollapsed);
    }
    target.setAttribute("aria-hidden", "true");
    if (this.config.closeButtonText && this.config.defaultButtonText) {
      const btnText = trigger.querySelector(this._buttonTextTarget);
      if (btnText instanceof HTMLElement) {
        btnText.textContent = this.config.defaultButtonText;
      }
    }
    if (this.config.disableScroll) {
      bodyScrollLock.clearAllBodyScrollLocks();
    }
    trigger.focus();
    this.element.dispatchEvent(new Event("closeMrToggle"));
  }
  handleMouseLeave() {
    this.close();
    if (!this.config.trigger) {
      return;
    }
    const trigger = document.querySelector(this.config.trigger);
    trigger?.blur();
  }
  handleKeyboard(event) {
    if (event.key === "Escape") {
      this.close();
    }
    if (this.config.disableTrap) {
      return;
    }
    if (event.key === "Tab" && this.config.trigger) {
      if (document.querySelector(this.config.trigger)?.getAttribute("aria-expanded") === "true") {
        this._tabFocus.trap(this.element, event);
      }
    }
  }
  /**
   * Resets all _triggers by setting aria-expanded to false and set their data-collapsed text
   */
  resetAllToggles() {
    this._triggers.forEach((toggle) => {
      toggle.setAttribute("aria-expanded", "false");
      const dataCollapsed = toggle.getAttribute("data-collapsed");
      if (dataCollapsed) {
        toggle.setAttribute("aria-label", dataCollapsed);
      }
      const closestModule = toggle.closest(`[data-module="${this._moduleName}"]`);
      const toggleConfig = closestModule?.dataset.config ? JSON.parse(closestModule.dataset.config) : null;
      if (toggleConfig?.closeButtonText && toggleConfig?.defaultButtonText) {
        const btnText = toggle.querySelector(this._buttonTextTarget);
        if (btnText instanceof HTMLElement) {
          btnText.textContent = toggleConfig.defaultButtonText ? toggleConfig.defaultButtonText : "";
        }
      }
      if (toggleConfig.disableScroll) {
        bodyScrollLock.clearAllBodyScrollLocks();
      }
    });
    this._targets.forEach((target) => {
      target.setAttribute("aria-hidden", "true");
    });
  }
};

// src/mr-tooltip.ts
var MrTooltip = class {
  element;
  options;
  id;
  main;
  modal;
  closeBtn;
  overlay;
  _responsiveHelper;
  _tabFocus;
  constructor(element, options) {
    this.element = element;
    this.options = options;
    this.main = document.querySelector("main");
    this.id = this.element.getAttribute("data-tooltip-id");
    this.modal = this.id ? document.getElementById(this.id) : null;
    this.closeBtn = this.modal ? this.modal.querySelector(".btn--js-tooltip-close") : null;
    this.overlay = this.modal ? this.modal.querySelector(".tooltip__overlay") : null;
    if (this.modal && this.options?.closeBtn) {
      this.closeBtn = this.modal.querySelector(`.${this.options.closeBtn}`);
    }
    this._responsiveHelper = new ResponsiveHelper();
    this._tabFocus = new TabFocusHelper();
    if (!this.id) {
      console.warn("MrTooltip: data-tooltip-id property not found on tooltip trigger button");
      return;
    }
    this.initialize();
  }
  initialize() {
    this.addEventListeners();
  }
  addEventListeners() {
    this.element.addEventListener("click", this.open.bind(this));
    this.closeBtn?.addEventListener("click", this.close.bind(this));
    this.overlay?.addEventListener("click", this.close.bind(this));
    this.modal?.addEventListener("keydown", this.keyDown.bind(this));
  }
  open(event) {
    if (!this.modal) {
      return;
    }
    const titleSelector = this.options?.title ? this.options?.title : "#tooltip__title";
    const descriptionSelector = this.options?.description ? this.options?.description : "#tooltip__description";
    const _title = this.modal.querySelector(titleSelector);
    const _description = this.modal.querySelector(descriptionSelector);
    if (!_title) {
      console.warn("MrTooltip: no title found, this is required");
      return;
    }
    const target = event.currentTarget;
    const title = this.element.getAttribute("data-title");
    const description = this.element.getAttribute("data-description");
    if (this.options?.activeTriggerClass) {
      target.classList.add(this.options.activeTriggerClass);
    } else {
      target.classList.add("js-tooltip--is-open");
    }
    this.main?.setAttribute("aria-hidden", "true");
    this.modal.setAttribute("aria-hidden", "false");
    while (_title?.firstChild) {
      _title?.removeChild(_title?.firstChild);
    }
    while (_description?.firstChild) {
      _description?.removeChild(_description?.firstChild);
    }
    _title.textContent = title;
    if (_description) {
      _description.innerHTML = this.convertStringToHtml(description);
    }
    this.setModalPosition(this.element, this.modal);
    ["resize", "orientationchange"].forEach((e) => {
      window.addEventListener(e, () => {
        if (this.modal) {
          this.setModalPosition(this.element, this.modal);
        }
      });
    });
    window.modalTrigger = this.element;
    const firstTabbable = getFirstFocusableElement(this.modal);
    firstTabbable?.focus();
    this.element.dispatchEvent(new Event("openMrTooltip"));
  }
  close() {
    this.main?.removeAttribute("aria-hidden");
    this.modal?.setAttribute("aria-hidden", "true");
    window.modalTrigger?.focus();
    window.modalTrigger?.classList.remove("js-tooltip--is-open");
    this.element.dispatchEvent(new Event("closeMrTooltip"));
  }
  // Calculate the modals position based on getBoundingClientRect of the tooltip button
  setModalPosition(button, modal) {
    const adminbar = document.querySelector("#wpadminbar");
    let adminBarHeight = 0;
    if (adminbar) {
      adminBarHeight = adminbar.offsetHeight;
    }
    if (this._responsiveHelper.atLeastTabletPortrait()) {
      modal.style.width = "auto";
      const buttonCoords = button.getBoundingClientRect();
      const modalWidth = modal.offsetWidth;
      const buttonHeight = buttonCoords.height;
      const modalHeight = modal.getBoundingClientRect().height;
      const windowWidth = window.innerWidth;
      const offsetY = 15;
      let offsetX = 50;
      let offsetXRight = 0;
      if (windowWidth / 2 <= buttonCoords.left) {
        if (modalWidth / 2 > windowWidth - buttonCoords.right - buttonCoords.width) {
          offsetXRight = 100;
        }
        modal.style.left = buttonCoords.left + buttonCoords.width / 2 - modalWidth / 2 - offsetXRight + "px";
      } else {
        if (buttonCoords.left > modalWidth / 2) {
          offsetX = modalWidth / 2;
        }
        modal.style.left = buttonCoords.left + buttonCoords.width / 2 - offsetX + "px";
      }
      let verticalOffset = 0;
      if (this.options?.verticalOffset) {
        verticalOffset = this.options?.verticalOffset;
      }
      if (this.options?.belowTrigger) {
        modal.style.top = button.getBoundingClientRect().bottom + window.scrollY + buttonHeight - offsetY - adminBarHeight + verticalOffset + "px";
      } else {
        modal.style.top = button.getBoundingClientRect().top + window.scrollY - modalHeight - offsetY - adminBarHeight - verticalOffset + "px";
      }
      modal.style.width = modalWidth + 1 + "px";
    } else {
      modal.style.top = adminBarHeight + "px";
      modal.style.left = "0";
      modal.style.width = "100%";
    }
  }
  keyDown(event) {
    if (event.key === "Tab" && this.modal) {
      this._tabFocus.trap(this.modal, event);
    }
    if (event.key === "Escape") {
      this.close();
    }
  }
  // Create HTML from escaped HTML string
  convertStringToHtml(escapedHtml) {
    if (!escapedHtml) {
      return "";
    }
    const e = document.createElement("div");
    e.innerHTML = escapedHtml;
    return e.childNodes[0].nodeValue ?? "";
  }
};

// src/dynamic-color.ts
var GetDynamicColor = (bgColor, options) => {
  if (!bgColor.match(/^#[a-f0-9]{6}$/i)) {
    throw new Error("GetDynamicColor: Color must be valid hex code");
  }
  if (options && !options?.darkColor?.match(/^#[a-f0-9]{6}$/i) && !options?.lightColor?.match(/^#[a-f0-9]{6}$/i)) {
    throw new Error("GetDynamicColor: Supplied custom color must be valid hex code");
  }
  const whiteContrast = GetContrast("#ffffff", bgColor);
  const blackContrast = GetContrast("#000000", bgColor);
  return whiteContrast > blackContrast ? options?.lightColor ? options.lightColor : "#ffffff" : options?.darkColor ? options.darkColor : "#000000";
};
var getRGB = (c) => {
  return parseInt(c, 16);
};
var getsRGB = (c) => {
  return getRGB(c) / 255 <= 0.03928 ? getRGB(c) / 255 / 12.92 : Math.pow((getRGB(c) / 255 + 0.055) / 1.055, 2.4);
};
var getLuminance = (hexColor) => {
  return 0.2126 * getsRGB(hexColor.substring(1, 3)) + 0.7152 * getsRGB(hexColor.substring(3, 5)) + 0.0722 * getsRGB(hexColor.substring(5));
};
var GetContrast = (foregroundColor, backgroundColor) => {
  if (!foregroundColor.match(/^#[a-f0-9]{6}$/i)) {
    throw new Error("GetContrast: Foreground color must be valid hex code");
  }
  if (!backgroundColor.match(/^#[a-f0-9]{6}$/i)) {
    throw new Error("GetContrast: Background color must be valid hex code");
  }
  const L1 = getLuminance(foregroundColor);
  const L2 = getLuminance(backgroundColor);
  return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
};
var DateRangeValidator = class {
  form;
  options;
  constructor(form, options) {
    this.form = form;
    this.options = options;
    this.addEventListeners();
  }
  addEventListeners() {
    this.form?.addEventListener("submit", this.handleSubmit.bind(this));
  }
  handleSubmit(event) {
    event.preventDefault();
    this.removePossibleErrorNotices();
    this.removePossibleErrorValidationSummary();
    const schema = zod.z.object({
      s: zod.z.string().optional(),
      post_date_min: zod.z.string().optional().nullable().refine(() => {
        return !this.options.minInput?.validity.badInput;
      }, this.options.errorMessages.minInput || "Invalid date entered"),
      post_date_max: zod.z.string().optional().nullable().refine(() => {
        return !this.options.maxInput?.validity.badInput;
      }, this.options.errorMessages.maxInput || "Invalid date entered")
    });
    try {
      schema.parse(new FormData(this.form));
      if (this.options.submitFormWhenValid) {
        this.form.submit();
      } else {
        document.dispatchEvent(new Event("DateRangeValidator:valid"));
      }
    } catch (error) {
      this.createErrorNoticesPerField(error);
      this.createValidationSummary(error);
      document.dispatchEvent(new Event("DateRangeValidator:invalid"));
    }
  }
  /**
   * Create the validation summary element
   */
  createValidationSummary(error) {
    if (!(error instanceof zod.ZodError)) {
      return;
    }
    const zodErrors = error.issues;
    if (zodErrors.length > 0) {
      const errorSummary = document.createElement("div");
      errorSummary.setAttribute("data-component", "drv-validation-summary");
      errorSummary.className = this.options.styles.validationSummary || "";
      const title = document.createElement("p");
      title.className = this.options.styles.validationSummaryTitle || "";
      title.textContent = this.options.errorMessages.summary || "There is a problem with your request. Check the notifications below.";
      title.setAttribute("data-component", "drv-validation-summary-title");
      title.setAttribute("role", "alert");
      title.setAttribute("tabindex", "-1");
      const list = document.createElement("ol");
      list.setAttribute("data-component", "drv-validation-summary-list");
      list.className = this.options.styles.validationSummaryList || "";
      zodErrors.forEach((issue) => {
        const item = document.createElement("li");
        item.className = this.options.styles.validationSummaryListItem || "";
        const link = document.createElement("a");
        link.className = this.options.styles.validationSummaryLink || "";
        const label = this.form.querySelector(`[for="${issue.path[0]}"]`)?.textContent;
        link.href = `#${issue.path[0]}`;
        link.textContent = `${label}: ${issue.message}`;
        item.appendChild(link);
        list.appendChild(item);
      });
      errorSummary.appendChild(title);
      errorSummary.appendChild(list);
      this.form.insertAdjacentElement("beforebegin", errorSummary);
      title.focus();
    }
  }
  /**
   * Creates the error message per invalid field
   */
  createErrorNoticesPerField(error) {
    if (!(error instanceof zod.ZodError)) {
      return;
    }
    const zodErrors = error.issues;
    zodErrors.forEach((issue) => {
      const inputErrorMessage = document.createElement("div");
      const id = `${issue.path}-error`;
      inputErrorMessage.className = this.options.styles.inputErrorMessage || "";
      inputErrorMessage.setAttribute("data-component", "drv-input-error");
      inputErrorMessage.id = id;
      const iconElement = document.createElement("span");
      iconElement.className = `icon-inline ${this.options.styles.inputErrorIcon || ""}`;
      iconElement.setAttribute("aria-hidden", "true");
      iconElement.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 14a1 1 0 0 1-1-1V7a1 1 0 1 1 2 0v6a1 1 0 0 1-1 1Z"></path><path d="M13.2 17a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0Z"></path><path fill-rule="evenodd" clip-rule="evenodd" d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Zm0 2c6.627 0 12-5.373 12-12S18.627 0 12 0 0 5.373 0 12s5.373 12 12 12Z"></path></svg>';
      const messageElement = document.createElement("span");
      messageElement.className = this.options.styles.inputErrorMessageLabel || "";
      messageElement.textContent = issue.message;
      inputErrorMessage.appendChild(iconElement);
      inputErrorMessage.appendChild(messageElement);
      const input = this.form.querySelector(`[name="${issue.path}"]`);
      input?.insertAdjacentElement("afterend", inputErrorMessage);
      input?.setAttribute("aria-describedby", id);
    });
  }
  /**
   * Remove all error notices if they exist
   */
  removePossibleErrorNotices() {
    const existingErrorList = this.form.querySelectorAll('[data-component="drv-input-error"]');
    if (existingErrorList) {
      existingErrorList.forEach((error) => error.remove());
    }
  }
  /**
   * Remove the error validation if it exist
   */
  removePossibleErrorValidationSummary() {
    const existingErrorValidation = this.form.parentElement?.querySelector('[data-component="drv-validation-summary"]');
    if (existingErrorValidation) {
      existingErrorValidation.remove();
    }
  }
};

exports.DateRangeValidator = DateRangeValidator;
exports.GetDynamicColor = GetDynamicColor;
exports.MrModal = MrModal;
exports.MrToggle = MrToggle;
exports.MrTooltip = MrTooltip;
exports.ResponsiveHelper = ResponsiveHelper;
exports.TabFocusHelper = TabFocusHelper;
exports.getAllFocusableElements = getAllFocusableElements;
exports.getFirstFocusableElement = getFirstFocusableElement;
//# sourceMappingURL=out.js.map
//# sourceMappingURL=index.cjs.map